import React, { ChangeEvent, useState } from 'react';
import styled from 'styled-components';

import { truncNumber } from '../../helpers/utils';
import { ComponentRootEx, Defaults, px } from '../common';

export interface InputExProps {
  type: 'number' | 'text' | 'password' | 'email';
  value?: any;
  minWidth?;
  width?;
  height?: number | string;
  flexGrow?: number;
  top?: number | string;
  left?: any;
  fontSize?: number | string;
  fontColor?: string;
  fontFamily?: string;
  textAlign?: string;
  borderColor?: string;
  backgroundColor?: string;
  borderRadius?: number | string;
  img?: React.ReactNode;
  imgWidth?: number | string;
  imgPos?: 'left' | 'right';
  placeholder?: React.ReactNode;
  hint?: string;
  onValidationResult?: (value) => void;
  onChange?: (value) => void;
  onInputClick?: (e) => void;
  onKeyPress?: (e) => void;
  onKeyDown?: (e) => void;
  pattern?: string;
  className?: string;
  autoFocus?: boolean;
  children?: React.ReactNode;
  hoverHint?: string;
  maxFracDigits?: number;
  maxLength?: number;
  disabled?: boolean;
}

const InputEx = ({
  type,
  value,
  minWidth,
  width,
  height,
  flexGrow,
  disabled = false,
  top,
  left,
  fontSize,
  fontColor,
  fontFamily,
  textAlign,
  borderColor,
  backgroundColor,
  borderRadius,
  img,
  imgWidth,
  imgPos = 'right',
  placeholder,
  hint,
  onChange,
  onValidationResult,
  onInputClick,
  onKeyPress,
  onKeyDown,
  pattern,
  autoFocus,
  children,
  hoverHint,
  className,
  maxFracDigits,
  maxLength = 10000,
}: InputExProps) => {
  const [edit, setEdit] = useState(false);
  const [isValid, setIsValid] = useState(true);

  function onChangeValue(event: ChangeEvent<HTMLInputElement>) {
    let newValue: any = event.target.value;
    if (type === 'number') {
      if (newValue === '') {
        newValue = undefined;
      }
      if (newValue !== undefined) {
        const f = Number.parseFloat(newValue);
        newValue = maxFracDigits !== undefined ? truncNumber(f, maxFracDigits) : f;
      }
    }
    if (onChange) {
      onChange(newValue);
    }
    if (pattern) {
      const result = RegExp(pattern).test(newValue);
      setIsValid(result);
      if (onValidationResult) {
        onValidationResult(result);
      }
    }
  }

  return (
    <InputRoot
      className={className}
      minWidth={minWidth}
      width={width}
      height={height}
      flexGrow={flexGrow}
      top={top}
      left={left}
      fontSize={fontSize}
      fontColor={fontColor}
      fontFamily={fontFamily}
      textAlign={textAlign}
      imgPos={imgPos}
      imgWidth={imgWidth}
      cursor={'default'}
      isValid={isValid}
      borderColor={borderColor}
      borderRadius={borderRadius}
      onBlur={() => setEdit(false)}
      backgroundColor={backgroundColor}
      onClick={() => setEdit(true)}>
      {imgPos === 'left' && !!imgWidth ? (
        <ImgWrap width={imgWidth}>{img}</ImgWrap>
      ) : undefined}
      {!!placeholder && !edit ? (
        <Placeholder hidden={!edit}>{placeholder}</Placeholder>
      ) : (
        <input
          type={type}
          value={value}
          readOnly={!onChange || disabled}
          placeholder={hint}
          pattern={pattern}
          onChange={onChangeValue}
          onBlur={() => setEdit(false)}
          onClick={onInputClick}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          width={'100%'}
          maxLength={maxLength}
          title={hoverHint}
        />
      )}
      {imgPos === 'right' && !!imgWidth ? (
        <ImgWrap borderColor={borderColor} width={imgWidth}>
          {img}
        </ImgWrap>
      ) : undefined}
      {children ? children : undefined}
    </InputRoot>
  );
};

export default InputEx;

const InputRoot = styled(ComponentRootEx)`
  overflow: hidden;
  ${(props) => (props.flexGrow ? `flex-grow: ${props.flexGrow};` : '')}
  ${(props) =>
    props.backgroundColor ? `background-color: ${props.backgroundColor};` : ''}
    ${(props) => (props.top ? `margin-top: ${px(props.top)};` : '')}
    ${(props) => (props.left ? `margin-left: ${px(props.left)};` : '')}
    ${(props) => (props.borderRadius ? `border-radius: ${px(props.borderRadius)};` : '')}

    & input {
    width: calc(100% - ${(props) => px(props.imgWidth || '0vh')} - 1vh);
    height: 100%;
    border: unset;
    padding: 0 0.4vh;
    margin: 0 0.4vh 0 0.4vh;
    font-family: ${(props) => props.fontFamily || 'inherit'};
    font-size: ${(props) => px(props.fontSize || Defaults.fontSize)};
    color: ${(props) => props.fontColor};
    background-color: inherit;
    ${(props) => (props.imgPos === 'left' ? 'padding-left: 0;' : '')}
    ${(props) => (props.imgPos === 'left' ? 'margin-left: 0;' : '')}
        ${(props) => (props.textAlign ? `text-align: ${props.textAlign};` : '')}
  }
  & input:focus {
    outline: none;
  }
  & input::-webkit-inner-spin-button,
  & input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  & input::placeholder {
    font-size: 14px;
    color: #00000029;
  }
  &:hover {
    background-color: ${(props) => props.backgroundColor || 'unset'};
  }
`;
const Placeholder = styled.div`
  display: flex;
  width: 100%;
  padding-left: 4px;
  color: #00000029;
`;
const ImgWrap = styled.div`
  display: flex;
  flex-flow: column-reverse;
  flex-shrink: 0;
  height: 100%;
  ${(props) => (props.width ? `width: ${px(props.width)};` : '')}
  ${(props) =>
    props.borderColor ? `border-left: 0.1vh solid ${props.borderColor};` : ''};
  justify-content: center;
  align-items: center;
`;
